import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sparkles, Loader2, ChevronRight, TrendingUp, DollarSign, Home } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface Topic {
  id: number;
  topic_name: string;
  topic_description: string;
  relative_interest: number;
  avg_monthly_searches: number;
  cpc_avg: number;
  confidence_score: number;
}

interface Sector {
  id: number;
  name: string;
  level: number;
  is_enriched: boolean;
  last_enriched_at: string | null;
}

interface ParentSector {
  id: number;
  name: string;
}

interface SectorData {
  sector: Sector;
  parent_sector: ParentSector | null;
  topics: Topic[];
}

const COLORS = [
  '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
  '#14b8a6', '#6366f1', '#a855f7', '#f97316', '#06b6d4',
  '#84cc16', '#eab308', '#ef4444', '#64748b', '#06b6d4'
];

export function SectorDemandExplorer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [data, setData] = useState<SectorData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: number; name: string }[]>([]);

  const currentSectorId = searchParams.get('sectorId') || '1'; // Default to first sector
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    loadSectorData();
  }, [currentSectorId]);

  const loadSectorData = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API_URL}/sectors/${currentSectorId}/topics`);
      const sectorData: SectorData = await response.json();
      setData(sectorData);

      // Build breadcrumbs
      buildBreadcrumbs(sectorData);
    } catch (error) {
      console.error('Error loading sector data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sector data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const buildBreadcrumbs = async (sectorData: SectorData) => {
    const crumbs: { id: number; name: string }[] = [];
    crumbs.push({ id: sectorData.sector.id, name: sectorData.sector.name });

    // Walk up the parent chain
    let parent = sectorData.parent_sector;
    while (parent) {
      crumbs.unshift({ id: parent.id, name: parent.name });
      try {
        const response = await authFetch(`${API_URL}/sectors/${parent.id}/topics`);
        const parentData: SectorData = await response.json();
        parent = parentData.parent_sector;
      } catch {
        break;
      }
    }

    setBreadcrumbs(crumbs);
  };

  const handleEnrich = async () => {
    if (!data) return;

    setIsEnriching(true);
    toast({
      title: 'Enriching Sector',
      description: 'Generating topics with demand data from Google Ads...',
    });

    try {
      const response = await authFetch(
        `${API_URL}/sectors/${data.sector.id}/discover-adjacent-topics`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result = await response.json();

      toast({
        title: 'Success! 🎉',
        description: `Generated ${result.topicsGenerated} topics${result.enrichedWithDemandData ? ' with demand data' : ''}.`,
      });

      // Reload data
      await loadSectorData();
    } catch (error) {
      console.error('Error enriching sector:', error);
      toast({
        title: 'Error',
        description: 'Failed to enrich sector with demand data',
        variant: 'destructive',
      });
    } finally {
      setIsEnriching(false);
    }
  };

  const handleTopicClick = async (topic: Topic) => {
    if (!data) return;

    // Promote topic to child sector
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${data.sector.id}/topics/${topic.id}/promote`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result = await response.json();

      toast({
        title: 'Sector Created',
        description: `"${topic.topic_name}" is now a sector. Navigating...`,
      });

      // Navigate to the new sector
      setSearchParams({ sectorId: result.newSector.id.toString() });
    } catch (error) {
      console.error('Error promoting topic:', error);
      toast({
        title: 'Error',
        description: 'Failed to create sector from topic',
        variant: 'destructive',
      });
    }
  };

  const handleBreadcrumbClick = (sectorId: number) => {
    setSearchParams({ sectorId: sectorId.toString() });
  };

  // Prepare chart data
  const chartData = data?.topics.map((topic, index) => ({
    name: topic.topic_name,
    value: topic.relative_interest || 1, // Use 1 if no interest data
    searches: topic.avg_monthly_searches,
    cpc: topic.cpc_avg,
    topic: topic,
    fill: COLORS[index % COLORS.length],
  })) || [];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-sm mb-1">{data.name}</p>
          <p className="text-xs text-gray-600">Interest: {data.value}%</p>
          {data.searches > 0 && (
            <>
              <p className="text-xs text-gray-600">
                Searches: {data.searches.toLocaleString()}/mo
              </p>
              {data.cpc > 0 && (
                <p className="text-xs text-gray-600">CPC: ${data.cpc.toFixed(2)}</p>
              )}
            </>
          )}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">Sector Demand Explorer</h1>
          <p className="text-gray-600">
            Visualize and explore search demand across product categories
          </p>
        </div>

        {isSuperAdmin && data && (
          <Button
            onClick={handleEnrich}
            disabled={isEnriching}
            className="flex items-center gap-2"
          >
            {isEnriching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Enrich Research
          </Button>
        )}
      </div>

      {/* Navigation */}
      {breadcrumbs.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id}>
              <button
                onClick={() => handleBreadcrumbClick(crumb.id)}
                className="hover:text-primary hover:underline"
              >
                {index === 0 && <Home className="h-4 w-4 inline mr-1" />}
                {crumb.name}
              </button>
              {index < breadcrumbs.length - 1 && <ChevronRight className="h-4 w-4" />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Main Content */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{data.sector.name}</CardTitle>
              <CardDescription>
                {data.sector.is_enriched
                  ? `Enriched on ${new Date(data.sector.last_enriched_at!).toLocaleDateString()}`
                  : 'Not yet enriched with demand data'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''
                      }
                      outerRadius={150}
                      fill="#8884d8"
                      dataKey="value"
                      onClick={(data) => handleTopicClick(data.topic)}
                      style={{ cursor: 'pointer' }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-96 text-gray-400">
                  <p className="text-lg mb-4">No topics yet</p>
                  {isSuperAdmin && (
                    <p className="text-sm">Click "Enrich Research" to generate topics</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Topics List */}
          <Card>
            <CardHeader>
              <CardTitle>Top Topics</CardTitle>
              <CardDescription>Click to drill down</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {chartData
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 10)
                  .map((item) => (
                    <div
                      key={item.topic.id}
                      onClick={() => handleTopicClick(item.topic)}
                      className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <p className="font-medium text-sm flex-1">{item.name}</p>
                        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      </div>
                      <div className="flex gap-4 text-xs text-gray-600">
                        {item.searches > 0 && (
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {item.searches.toLocaleString()}
                          </span>
                        )}
                        {item.cpc > 0 && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            ${item.cpc.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
